import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { OidcApplication } from "../shared/types";
import {
  adminQueries,
  adminQueryKeys,
  upsertSorted,
  useAdminApi,
} from "./admin-query";
import {
  EmptyState,
  MetricCard,
  PageHeader,
  PanelState,
  PanelToolbar,
  RefreshFailedNotice,
  type StatusFilter,
} from "./AdminUi";
import { errorMessage } from "./api";
import { formatDate } from "./admin-model";
import { ApplicationDialog } from "./ApplicationDialog";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { Icon } from "./Icon";
import { PaginationControls, usePagination } from "./pagination";
import { SecretDialog } from "./SecretDialog";
import type { ApplicationDraft } from "./types";

function health(application: OidcApplication): string {
  if (application.status === "disabled") return "Disabled";
  if (!application.secretConfigured) return "Needs attention";
  if (!application.lastTokenAt) return "Not verified";
  return "Healthy";
}

export function ApplicationsPanel() {
  const queryClient = useQueryClient();
  const adminApi = useAdminApi();
  const applicationsQuery = useQuery(adminQueries.applications());
  const applications = useMemo(
    () => applicationsQuery.data?.applications ?? [],
    [applicationsQuery.data?.applications],
  );
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [editing, setEditing] = useState<OidcApplication | null | undefined>();
  const [rotating, setRotating] = useState<OidcApplication>();
  const [visibleSecret, setVisibleSecret] = useState<{
    clientId: string;
    mode: "issued" | "revealed";
    value: string;
  }>();

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return applications.filter((application) => {
      const matchesStatus = status === "all" || application.status === status;
      const matchesQuery =
        !needle ||
        [
          application.displayName,
          application.clientId,
          application.owner,
          application.environment,
        ]
          .join(" ")
          .toLowerCase()
          .includes(needle);
      return matchesStatus && matchesQuery;
    });
  }, [applications, query, status]);
  const [pageSlice, setPage] = usePagination(
    filtered,
    25,
    JSON.stringify([query, status]),
  );

  function merge(application: OidcApplication) {
    queryClient.setQueryData<{ applications: OidcApplication[] }>(
      adminQueryKeys.applications,
      (current) => ({
        applications: upsertSorted(
          current?.applications ?? [],
          application,
          (candidate) => candidate.clientId,
          (candidate) => candidate.displayName,
        ),
      }),
    );
  }

  const saveMutation = useMutation({
    mutationFn: (draft: ApplicationDraft) =>
      adminApi<{ application: OidcApplication; clientSecret?: string }>(
        editing
          ? `/api/admin/applications/${encodeURIComponent(editing.clientId)}`
          : "/api/admin/applications",
        {
          method: editing ? "PATCH" : "POST",
          body: JSON.stringify({
            ...draft,
            ...(editing ? { expectedRevision: editing.revision } : {}),
          }),
        },
      ),
    onSuccess: (result) => {
      merge(result.application);
      setEditing(undefined);
      if (result.clientSecret) {
        setVisibleSecret({
          clientId: result.application.clientId,
          mode: "issued",
          value: result.clientSecret,
        });
      }
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (application: OidcApplication) =>
      adminApi<{ application: OidcApplication; clientSecret: string }>(
        `/api/admin/applications/${encodeURIComponent(application.clientId)}/rotate-secret`,
        {
          method: "POST",
          body: JSON.stringify({
            expectedRevision: application.revision,
            overlapSeconds: 900,
          }),
        },
      ),
    // A failure keeps the confirmation open so it can report the reason.
    onSuccess: (result) => {
      merge(result.application);
      setRotating(undefined);
      setVisibleSecret({
        clientId: result.application.clientId,
        mode: "issued",
        value: result.clientSecret,
      });
    },
  });

  const revealMutation = useMutation({
    mutationFn: (application: OidcApplication) =>
      adminApi<{ clientSecret: string }>(
        `/api/admin/applications/${encodeURIComponent(application.clientId)}/reveal-secret`,
        {
          method: "POST",
          body: JSON.stringify({ expectedRevision: application.revision }),
        },
      ),
    onSuccess: (result, application) =>
      setVisibleSecret({
        clientId: application.clientId,
        mode: "revealed",
        value: result.clientSecret,
      }),
  });

  function edit(application: OidcApplication | null) {
    saveMutation.reset();
    setEditing(application);
  }

  function rotate(application: OidcApplication) {
    rotateMutation.reset();
    setRotating(application);
  }

  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel (and any open secret dialog) on screen.
  if (!applicationsQuery.data) {
    return (
      <PanelState error={applicationsQuery.isError} label="applications" />
    );
  }

  const busy = saveMutation.isPending || rotateMutation.isPending;
  const error =
    (saveMutation.error &&
      errorMessage(saveMutation.error, "Unable to save application.")) ||
    (rotateMutation.error &&
      errorMessage(rotateMutation.error, "Unable to rotate secret.")) ||
    (revealMutation.error &&
      errorMessage(revealMutation.error, "Unable to reveal secret.")) ||
    "";
  return (
    <>
      <PageHeader
        action={
          <button
            className="button button--primary"
            onClick={() => edit(null)}
            type="button"
          >
            <Icon name="plus" size={16} />
            Register application
          </button>
        }
        description="Register OIDC clients, callbacks, claims, login modes, and token policies."
        eyebrow="Relying parties"
        title="Applications"
      />

      {applicationsQuery.isError && <RefreshFailedNotice />}

      {error && editing === undefined && (
        <div className="notice notice--error page-notice" role="alert">
          {error}
        </div>
      )}

      <section className="stat-grid" aria-label="Application summary">
        <MetricCard
          detail="Registered client IDs"
          label="Total applications"
          value={applications.length}
        />
        <MetricCard
          detail="Accepting authorization"
          label="Active"
          tone="positive"
          value={applications.filter((item) => item.status === "active").length}
        />
        <MetricCard
          detail="Password-only integrations"
          label="Fixed account"
          tone={
            applications.some((item) => item.loginMode === "fixed")
              ? "warning"
              : "neutral"
          }
          value={
            applications.filter((item) => item.loginMode === "fixed").length
          }
        />
      </section>

      <section className="directory-card">
        <header className="collection-header">
          <div>
            <h2>Registered applications</h2>
            <p>
              OIDC clients allowed to request authorization from this provider.
            </p>
          </div>
          <span>{applications.length}</span>
        </header>
        <PanelToolbar
          filterLabel="Filter applications by status"
          onQueryChange={setQuery}
          onStatusChange={setStatus}
          query={query}
          searchLabel="Search applications"
          searchPlaceholder="Search name, client ID, owner…"
          status={status}
        />
        <div className="table-wrap">
          <table className="applications-table">
            <caption className="sr-only">Registered OIDC applications</caption>
            <thead>
              <tr>
                <th>Application</th>
                <th>Health</th>
                <th>Login</th>
                <th>Authentication</th>
                <th>Last token</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageSlice.items.map((application) => (
                <tr key={application.clientId}>
                  <td data-label="Application">
                    <div className="application-cell">
                      <strong>{application.displayName}</strong>
                      <code>{application.clientId}</code>
                      <small>
                        {application.owner} · {application.environment} ·{" "}
                        {application.redirectUris.length} callback
                        {application.redirectUris.length === 1 ? "" : "s"}
                      </small>
                    </div>
                  </td>
                  <td data-label="Health">
                    <span
                      className={`health health--${health(application).toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {health(application)}
                    </span>
                  </td>
                  <td data-label="Login">
                    {application.loginMode === "fixed"
                      ? `Fixed · ${application.fixedUsername}`
                      : "Directory"}
                  </td>
                  <td data-label="Authentication">
                    <code>{application.tokenEndpointAuthMethod}</code>
                  </td>
                  <td className="muted" data-label="Last token">
                    {formatDate(application.lastTokenAt)}
                  </td>
                  <td data-label="Actions">
                    <div className="row-actions">
                      {application.tokenEndpointAuthMethod !== "none" && (
                        <>
                          <button
                            className="button button--quiet"
                            disabled={
                              busy ||
                              revealMutation.isPending ||
                              !application.secretRevealable
                            }
                            onClick={() => revealMutation.mutate(application)}
                            title={
                              application.secretRevealable
                                ? "Reveal the current client secret"
                                : "Rotate once to enable encrypted reveal"
                            }
                            type="button"
                          >
                            <Icon name="eye" size={15} />
                            {revealMutation.isPending &&
                            revealMutation.variables?.clientId ===
                              application.clientId
                              ? "Revealing…"
                              : "Reveal"}
                          </button>
                          <button
                            className="button button--quiet"
                            disabled={busy || revealMutation.isPending}
                            onClick={() => rotate(application)}
                            type="button"
                          >
                            <Icon name="rotate" size={15} />
                            Rotate
                          </button>
                        </>
                      )}
                      <button
                        className="button button--quiet"
                        onClick={() => edit(application)}
                        type="button"
                      >
                        Configure
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <EmptyState
              hint="Change the filters or register a new client."
              onClearFilters={
                query || status !== "all"
                  ? () => {
                      setQuery("");
                      setStatus("all");
                    }
                  : undefined
              }
              title="No applications found"
            />
          )}
        </div>
        <PaginationControls
          label="applications"
          onPageChange={setPage}
          slice={pageSlice}
        />
      </section>

      {editing !== undefined && (
        <ApplicationDialog
          application={editing ?? undefined}
          busy={saveMutation.isPending}
          key={editing?.clientId ?? "new"}
          error={
            saveMutation.error
              ? errorMessage(saveMutation.error, "Unable to save application.")
              : ""
          }
          onClose={() => !saveMutation.isPending && setEditing(undefined)}
          onSave={(draft) => saveMutation.mutate(draft)}
        />
      )}
      {visibleSecret && (
        <SecretDialog
          clientId={visibleSecret.clientId}
          clientSecret={visibleSecret.value}
          mode={visibleSecret.mode}
          onClear={() => setVisibleSecret(undefined)}
        />
      )}
      {rotating && (
        <ConfirmationDialog
          busy={rotateMutation.isPending}
          confirmLabel="Rotate secret"
          description={`A new secret will be issued for ${rotating.displayName}. The current secret remains valid for 15 minutes so you can update the relying party.`}
          error={
            rotateMutation.error
              ? errorMessage(rotateMutation.error, "Unable to rotate secret.")
              : ""
          }
          onCancel={() => !rotateMutation.isPending && setRotating(undefined)}
          onConfirm={() => rotateMutation.mutate(rotating)}
          title="Rotate client secret?"
        />
      )}
    </>
  );
}
